/**
 * Licence exiAdmin d'une organisation — issue #73 : la moitié manquante du branchement fait
 * côté serveur (`organizations.service.js::getSeatsOverview`/`setLicenceKey`).
 *
 * `licence.managed: false` est l'état NORMAL de la majorité des organisations : le quota vient
 * alors simplement de la base, ce n'est ni une erreur ni un signal d'alerte. `grace` mérite un
 * avertissement visible (accent + icône + texte, jamais la couleur seule) : l'exploitation est
 * en sursis pendant 72 h si exiAdmin est injoignable ou son statut non confirmé.
 *
 * La clé ne ressort jamais en clair — le serveur ne rend que `licenceKeyHint` — et le champ de
 * saisie est vidé après enregistrement : il ne réaffiche jamais ce qui vient d'être posé.
 *
 * Droit d'écriture reproduit à l'identique du serveur (`canManageSeats`) : `proprietaire` ou `facturation`
 * seulement, passé ici en prop `canManage` — jamais recalculé localement, pour rester la seule
 * source de vérité avec `OrganizationPage`.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { appAlert, appConfirm } from '../dialogs/service.js';
import { BTN_GHOST, FORM_FIELD, FORM_LABEL, FORM_ROW, INPUT, TAG } from '../ui/theme.js';

const STATUS_LABEL_KEYS = {
  unconfigured: 'organization.seats.licence.statusLabel.unconfigured',
  active: 'organization.seats.licence.statusLabel.active',
  grace: 'organization.seats.licence.statusLabel.grace',
  blocked: 'organization.seats.licence.statusLabel.blocked',
};

function formatDate(iso, locale) {
  if (!iso) return null;
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  );
}

export function LicencePanel({ organizationId, licence, canManage, onChanged }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language?.split('-')[0];
  const [keyInput, setKeyInput] = useState('');
  const [busy, setBusy] = useState(false);

  if (!licence) return null;

  const { managed, status, reason, graceExpiresAt, licenceKeyHint } = licence;
  const emphasised = status === 'grace' || status === 'blocked';

  const submit = async (event) => {
    event.preventDefault();
    const trimmed = keyInput.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      await api.patch(`/organizations/${organizationId}/licence`, { licenceKey: trimmed });
      setKeyInput('');
      await onChanged?.();
    } catch (error) {
      await appAlert(t('organization.seats.licence.saveFailed', { message: error.message }), {
        title: t('organization.seats.licence.saveFailedTitle'),
      });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (
      !(await appConfirm(t('organization.seats.licence.removeConfirm'), {
        confirmLabel: t('organization.seats.licence.removeConfirmLabel'),
      }))
    )
      return;
    setBusy(true);
    try {
      await api.patch(`/organizations/${organizationId}/licence`, { licenceKey: '' });
      await onChanged?.();
    } catch (error) {
      await appAlert(t('organization.seats.licence.removeFailed', { message: error.message }), {
        title: t('organization.seats.licence.removeFailedTitle'),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex flex-col gap-3 border-t-2 border-line pt-4">
      <p className="font-mono text-[0.68rem] tracking-eyebrow text-ink-faint uppercase">
        {t('organization.seats.licence.eyebrow')}
      </p>

      <span
        className={`${TAG} w-fit ${
          emphasised
            ? 'border-accent-trait bg-accent-wash text-accent-ink'
            : 'border-line text-ink-soft'
        }`}
      >
        {emphasised && <span aria-hidden="true">▲</span>}
        {status === 'active' && <span aria-hidden="true">✓</span>}
        {t(STATUS_LABEL_KEYS[status] ?? STATUS_LABEL_KEYS.unconfigured)}
      </span>

      {!managed && (
        <p className="text-sm text-ink-soft">{t('organization.seats.licence.unmanaged')}</p>
      )}

      {status === 'grace' && (
        <div
          role="alert"
          className="flex items-center gap-2 border-2 border-accent-trait bg-accent-wash px-3 py-2 text-sm text-accent-ink"
        >
          <span aria-hidden="true">▲</span>
          <p>
            {t('organization.seats.licence.graceWarning', {
              date: formatDate(graceExpiresAt, locale) ?? graceExpiresAt,
              reason,
            })}
          </p>
        </div>
      )}

      {status === 'blocked' && (
        <div
          role="alert"
          className="flex items-center gap-2 border-2 border-accent-trait bg-accent-wash px-3 py-2 text-sm text-accent-ink"
        >
          <span aria-hidden="true">▲</span>
          <p>{t('organization.seats.licence.blockedWarning', { reason })}</p>
        </div>
      )}

      {licenceKeyHint && (
        <p className="text-sm text-ink">
          {t('organization.seats.licence.currentKeyLabel')} :{' '}
          <span className="font-mono">
            •••• {t('organization.seats.licence.keyHint', { hint: licenceKeyHint })}
          </span>
        </p>
      )}

      {canManage && (
        <>
          <form onSubmit={submit} className={FORM_ROW}>
            <label className={FORM_FIELD}>
              <span className={FORM_LABEL}>{t('organization.seats.licence.fieldLabel')}</span>
              <input
                aria-label={t('organization.seats.licence.fieldLabel')}
                className={INPUT}
                placeholder={t('organization.seats.licence.fieldPlaceholder')}
                value={keyInput}
                onChange={(event) => setKeyInput(event.target.value)}
                disabled={busy}
              />
            </label>
            <button type="submit" className={BTN_GHOST} disabled={busy || !keyInput.trim()}>
              {t('organization.seats.licence.save')}
            </button>
            {licenceKeyHint && (
              <button type="button" className={BTN_GHOST} disabled={busy} onClick={remove}>
                {t('organization.seats.licence.remove')}
              </button>
            )}
          </form>
          <p className="text-xs text-ink-faint">
            {t('organization.seats.licence.seatsNeverLowered')}
          </p>
        </>
      )}
    </section>
  );
}
